import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { HeartPulse, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import PatientRegistrationForm from '../../components/forms/PatientRegistrationForm';

export default function Register() {
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [role, setRole] = useState(location.state?.role || 'Therapist');
    const { register } = useAuth();
    const { addProfile } = useData();

    const handlePatientComplete = async (patientData) => {
        setIsLoading(true);
        try {
            // 1. Create clinical profile in DataContext
            const newProfile = addProfile({
                id: patientData.patientId, // Use the ID generated by the form
                name: patientData.childFullName,
                age: patientData.ageYears,
                diagnosis: 'Pending Evaluation',
                supportLevel: 'Level 1',
                ...patientData
            });

            // 2. Register user account in AuthContext
            await register({ 
                name: patientData.parentName, 
                email: patientData.email, 
                password: patientData.password, 
                role: 'Patient',
                patientId: newProfile.id, // LINKED ID
                patientName: patientData.childFullName, // ADDED for matching visibility
                ...patientData 
            });
        } catch (error) {
            console.error("Registration error:", error);
            alert("Registration failed. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        const data = new FormData(e.target);
        await register({ 
            name: data.get('firstName') + ' ' + data.get('lastName'), 
            email: data.get('email'), 
            password: data.get('password'), 
            role 
        });
        setIsLoading(false);
    };

    if (role === 'Patient') {
        return (
            <div className="min-h-screen bg-mesh-gradient pt-12 px-4 pb-20">
                <div className="max-w-4xl mx-auto mb-10 text-center">
                    <div className="flex justify-center mb-6">
                        <Link to="/Screening_test" className="flex items-center gap-2">
                            <HeartPulse className="h-10 w-10 text-brand-600" />
                            <span className="text-3xl font-bold tracking-tight text-slate-900 font-outfit">Melete-Nova</span>
                        </Link>
                    </div>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/50 backdrop-blur-md rounded-full border border-white/50 shadow-sm mb-6">
                        <HeartPulse className="text-brand-600" size={18} />
                        <span className="text-sm font-semibold text-brand-800">Child Registration (2-5 Years)</span>
                    </div>
                    <h1 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">Patient Account Creation</h1>
                    <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                        Complete your profile to get started with our world-class care platform.
                    </p>
                    <button 
                        onClick={() => setRole('Therapist')}
                        className="mt-4 text-sm font-medium text-brand-600 hover:text-brand-500 underline"
                    >
                        Register as Practitioner instead
                    </button>
                </div>
                <PatientRegistrationForm onComplete={handlePatientComplete} />
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 sm:px-6 lg:px-8 py-12">
            <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-premium border border-slate-100">
                <div className="text-center">
                    <HeartPulse className="mx-auto h-12 w-12 text-brand-600" />
                    <h2 className="mt-6 text-3xl font-extrabold text-slate-900">Create your account</h2>
                    <p className="mt-2 text-sm text-slate-600">
                        Already have an account?{' '}
                        <Link to="/Screening_test/login" className="font-medium text-brand-600 hover:text-brand-500">
                            Sign in
                        </Link>
                    </p>
                </div>

                <form className="mt-8 space-y-6" onSubmit={handleRegister}>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">I am a...</label>
                            <div className="grid grid-cols-1 gap-3">
                                <button
                                    type="button"
                                    onClick={() => setRole('Therapist')}
                                    className={`relative p-3 border rounded-xl text-left font-medium transition-all ${role === 'Therapist' ? 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-500' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                                >
                                    Therapist / Educator / Admin
                                    {role === 'Therapist' && <CheckCircle2 className="h-4 w-4 absolute right-3 top-3.5 text-brand-600" />}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRole('Patient')}
                                    className={`relative p-3 border rounded-xl text-left font-medium transition-all ${role === 'Patient' ? 'border-brand-500 bg-brand-50 text-brand-700 ring-1 ring-brand-500' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                                >
                                    Parent / Guardian / Patient
                                    {role === 'Patient' && <CheckCircle2 className="h-4 w-4 absolute right-3 top-3.5 text-brand-600" />}
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">First name</label>
                                <Input name="firstName" required placeholder="Jane" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Last name</label>
                                <Input name="lastName" required placeholder="Doe" />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Email address</label>
                            <Input name="email" type="email" required placeholder="jane@example.com" />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                            <Input name="password" type="password" required placeholder="Create a strong password" />
                        </div>
                    </div>

                    <Button type="submit" className="w-full" size="lg" isLoading={isLoading}>
                        Create Account
                    </Button>

                    <p className="text-xs text-center text-slate-500">
                        By creating an account, you agree to our Terms of Service. All data is securely encrypted.
                    </p>
                </form>
            </div>
        </div>
    );
}
